/**
 * Data Transformation Utilities
 * Generic utilities for transforming data between different formats
 * Used by: financial-metrics, detailed_view dashboards
 */

// ============ TYPES ============

export interface CategorySeriesData {
  category: string[];
  series: Array<{
    name: string;
    data: number[];
  }>;
}

export interface FlatDataItem {
  [key: string]: any;
}

export interface SeriesDataItem {
  name: string;
  type: string;
  stack?: string;
  emphasis?: { focus: string };
  data: number[];
}

export interface ChartDataResult {
  categories: string[];
  seriesData: SeriesDataItem[];
}

export interface DataTransformConfig {
  dataKey?: string;
  seriesKey?: string;
  categoryKey?: string;
  valueKey?: string;
  quarterKey?: string;
  yearKey?: string;
}

// ============ DATA PARSING UTILITIES ============

/**
 * Parse chart data from multiple formats into standard format
 * Supports: flat array, dictionary, category+series formats
 */
export function parseChartData(
  chartData: any,
  config: DataTransformConfig = {}
): ChartDataResult {
  const {
    dataKey,
    seriesKey = 'name',
    categoryKey,
    valueKey = 'value',
    quarterKey = 'quarter',
    yearKey = 'year',
  } = config;

  let categories: string[] = [];
  let seriesData: SeriesDataItem[] = [];

  // 1. GENERIC FLAT ARRAY FORMAT (with configurable keys)
  if (dataKey && chartData[dataKey] && Array.isArray(chartData[dataKey])) {
    const flatData = chartData[dataKey];
    const result = parseFlatArrayData(flatData, {
      seriesKey,
      quarterKey,
      yearKey,
      valueKey,
    });
    categories = result.categories;
    seriesData = result.seriesData;
  }
  // 2. DICTIONARY FORMAT (keys are series names)
  else if (!chartData.category && !chartData.series && typeof chartData === 'object') {
    const seriesNames = Object.keys(chartData);
    if (seriesNames.length > 0 && Array.isArray(chartData[seriesNames[0]])) {
      const result = parseDictionaryData(chartData, { quarterKey, yearKey });
      categories = result.categories;
      seriesData = result.seriesData;
    }
  }
  // 3. STANDARD FORMAT (category + series arrays)
  else if (chartData.category && chartData.series) {
    const result = parseStandardData(chartData);
    categories = result.categories;
    seriesData = result.seriesData;
  }

  return { categories, seriesData };
}

/**
 * Parse flat array data format
 * Example: [{ quarter: 'Q1', year: 2024, segment: 'Tech', value: 100 }, ...]
 */
export function parseFlatArrayData(
  flatData: FlatDataItem[],
  config: {
    seriesKey: string;
    quarterKey?: string;
    yearKey?: string;
    valueKey?: string;
    categoryFormatter?: (item: FlatDataItem) => string;
  }
): ChartDataResult {
  const {
    seriesKey,
    quarterKey = 'quarter',
    yearKey = 'year',
    valueKey = 'value',
    categoryFormatter,
  } = config;

  // Build categories
  const uniqueCategories = Array.from(
    new Set(
      flatData.map((d: FlatDataItem) =>
        categoryFormatter
          ? categoryFormatter(d)
          : `${d[quarterKey]} ${d[yearKey]}`
      )
    )
  ) as string[];

  // Build unique series names
  const uniqueSeriesNames = Array.from(
    new Set(flatData.map((d: FlatDataItem) => d[seriesKey] || 'Unknown'))
  ) as string[];

  // Build series data
  const seriesData: SeriesDataItem[] = uniqueSeriesNames.map((name) => {
    const seriesPoints = uniqueCategories.map((cat) => {
      const point = flatData.find((d: FlatDataItem) => {
        const itemCategory = categoryFormatter
          ? categoryFormatter(d)
          : `${d[quarterKey]} ${d[yearKey]}`;
        return d[seriesKey] === name && itemCategory === cat;
      });
      return point ? point[valueKey] : 0;
    });

    return {
      name: name,
      type: 'bar',
      stack: 'total',
      emphasis: { focus: 'series' },
      data: seriesPoints,
    };
  });

  return { categories: uniqueCategories, seriesData };
}

/**
 * Parse dictionary format data
 * Example: { "Tech": [{ quarter: 'Q1', year: 2024, value: 100 }], "Healthcare": [...] }
 */
export function parseDictionaryData(
  dictData: Record<string, FlatDataItem[]>,
  config: {
    quarterKey?: string;
    yearKey?: string;
    valueKey?: string;
  } = {}
): ChartDataResult {
  const { quarterKey = 'quarter', yearKey = 'year', valueKey = 'value' } = config;

  const seriesNames = Object.keys(dictData);
  let categories: string[] = [];

  // Get categories from first series
  if (seriesNames.length > 0) {
    const firstSeriesData = dictData[seriesNames[0]];
    if (Array.isArray(firstSeriesData)) {
      categories = firstSeriesData.map(
        (item: FlatDataItem) => `${item[quarterKey]} ${item[yearKey]}`
      );
    }
  }

  // Build series data
  const seriesData: SeriesDataItem[] = seriesNames.map((name) => {
    const rawData = dictData[name];
    return {
      name: name,
      type: 'bar',
      stack: 'total',
      emphasis: { focus: 'series' },
      data: Array.isArray(rawData) ? rawData.map((d: FlatDataItem) => d[valueKey]) : [],
    };
  });

  return { categories, seriesData };
}

/**
 * Parse standard format data (category + series)
 * Example: { category: ['Q1 2024', 'Q2 2024'], series: [{ name: 'Tech', data: [100, 200] }] }
 */
export function parseStandardData(standardData: CategorySeriesData): ChartDataResult {
  const categories = standardData.category;
  const seriesData: SeriesDataItem[] = standardData.series.map((s) => ({
    name: s.name,
    type: 'bar',
    stack: 'total',
    emphasis: { focus: 'series' },
    data: s.data,
  }));

  return { categories, seriesData };
}

// ============ DATA AGGREGATION UTILITIES ============

/**
 * Group data by a specific key
 */
export function groupBy<T extends Record<string, any>>(
  data: T[],
  key: keyof T
): Record<string, T[]> {
  return data.reduce((result, item) => {
    const groupKey = String(item[key]);
    if (!result[groupKey]) {
      result[groupKey] = [];
    }
    result[groupKey].push(item);
    return result;
  }, {} as Record<string, T[]>);
}

/**
 * Sum values by group
 */
export function sumByGroup<T extends Record<string, any>>(
  data: T[],
  groupKey: keyof T,
  valueKey: keyof T
): Record<string, number> {
  const groups = groupBy(data, groupKey);
  const result: Record<string, number> = {};

  Object.keys(groups).forEach((key) => {
    result[key] = groups[key].reduce((sum, item) => sum + (Number(item[valueKey]) || 0), 0);
  });

  return result;
}

/**
 * Pivot data - convert rows to columns
 */
export function pivotData<T extends Record<string, any>>(
  data: T[],
  rowKey: keyof T,
  columnKey: keyof T,
  valueKey: keyof T
): Record<string, Record<string, any>> {
  const result: Record<string, Record<string, any>> = {};

  data.forEach((item) => {
    const row = String(item[rowKey]);
    const col = String(item[columnKey]);
    const val = item[valueKey];

    if (!result[row]) {
      result[row] = {};
    }
    result[row][col] = val;
  });

  return result;
}

// ============ ARRAY UTILITIES ============

/**
 * Get unique values from array
 */
export function unique<T>(array: T[]): T[] {
  return Array.from(new Set(array));
}

/**
 * Flatten nested array to any depth
 */
export function flattenDeep<T>(arr: any[]): T[] {
  return arr.reduce(
    (acc, val) =>
      Array.isArray(val) ? acc.concat(flattenDeep(val)) : acc.concat(val),
    []
  );
}

/**
 * Sort array by property
 */
export function sortByProperty<T extends Record<string, any>>(
  array: T[],
  property: keyof T,
  order: 'asc' | 'desc' = 'asc'
): T[] {
  return [...array].sort((a, b) => {
    const valA = a[property];
    const valB = b[property];

    if (valA < valB) return order === 'asc' ? -1 : 1;
    if (valA > valB) return order === 'asc' ? 1 : -1;
    return 0;
  });
}

/**
 * Order array based on custom order array
 */
export function orderByCustom<T extends Record<string, any>>(
  array: T[],
  orderArray: string[],
  key: keyof T
): T[] {
  return [...array].sort((a, b) => {
    const indexA = orderArray.indexOf(String(a[key]));
    const indexB = orderArray.indexOf(String(b[key]));

    // Items not in orderArray go to the end
    const posA = indexA === -1 ? orderArray.length : indexA;
    const posB = indexB === -1 ? orderArray.length : indexB;

    return posA - posB;
  });
}

// ============ TABLE DATA UTILITIES ============

/**
 * Filter table data based on multiple criteria
 */
export function filterTableData<T extends Record<string, any>>(
  data: T[],
  filters: Record<string, any[]>
): T[] {
  return data.filter((row) => {
    return Object.entries(filters).every(([key, values]) => {
      if (!values || values.length === 0) return true;
      return values.includes(row[key]);
    });
  });
}

/**
 * Search table data (case-insensitive)
 */
export function searchTableData<T extends Record<string, any>>(
  data: T[],
  searchTerm: string,
  searchKeys: (keyof T)[]
): T[] {
  if (!searchTerm) return data;

  const term = searchTerm.toLowerCase().trim();

  return data.filter((row) => {
    return searchKeys.some((key) => {
      const value = String(row[key] || '').toLowerCase();
      return value.includes(term);
    });
  });
}

/**
 * Paginate array
 */
export function paginate<T>(array: T[], page: number, pageSize: number): T[] {
  const startIndex = (page - 1) * pageSize;
  return array.slice(startIndex, startIndex + pageSize);
}

/**
 * Get pagination info
 */
export function getPaginationInfo(
  totalItems: number,
  currentPage: number,
  pageSize: number
): {
  totalPages: number;
  startItem: number;
  endItem: number;
  hasNextPage: boolean;
  hasPreviousPage: boolean;
} {
  const totalPages = Math.ceil(totalItems / pageSize);
  const startItem = (currentPage - 1) * pageSize + 1;
  const endItem = Math.min(currentPage * pageSize, totalItems);

  return {
    totalPages,
    startItem,
    endItem,
    hasNextPage: currentPage < totalPages,
    hasPreviousPage: currentPage > 1,
  };
}

// ============ INLINE DATA EXTRACTION ============

/**
 * Check if data is inline (embedded in config) or needs to be fetched
 */
export function isInlineData(widgetConfig: any): boolean {
  return !!(widgetConfig.data && (widgetConfig.data.category || widgetConfig.data.series || Array.isArray(widgetConfig.data)));
}

/**
 * Extract data source info from widget config
 */
export function getDataSourceInfo(widgetConfig: any): {
  type: 'inline' | 'api' | 'mockUrl';
  source: any;
} {
  if (isInlineData(widgetConfig)) {
    return { type: 'inline', source: widgetConfig.data };
  }

  if (widgetConfig.apiUrl) {
    return { type: 'api', source: widgetConfig.apiUrl };
  }

  if (widgetConfig.mockDataUrl) {
    return { type: 'mockUrl', source: widgetConfig.mockDataUrl };
  }

  return { type: 'inline', source: null };
}